'use client';
import { cn } from '@/utils/classNames';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import {
    Children,
    cloneElement,
    forwardRef,
    isValidElement,
    useEffect,
    useMemo,
    useState,
} from 'react';
import IconValue from './IconValue';
import { InlineEditableText } from './InlineEditableText';

const THEME_ICON_VARIANTS = new Set(['primary', 'secondary']);
const BUTTON_VARIANTS = new Set([
    'primary',
    'secondary',
    'outline',
    'outlineDark',
    'ghost',
    'link',
]);
const BUTTON_SIZES = new Set(['sm', 'md', 'lg', 'xl']);
const BUTTON_HOVER_ANIMATIONS = new Set([
    'none',
    'glow',
    'gradual-fill',
    'slide-up',
    'slide-right',
    'shimmer',
    'rotating-border',
    'magnet',
    'underline-draw',
    'gradient-shift',
    'pulse-ring',
    'tilt',
    'icon-slide',
    'icon-nudge',
    'icon-fill-slide',
    'icon-show',
    'icon-reveal',
]);

const normalizeButtonVariant = (input) => {
    const rawValue =
        input && typeof input === 'object' && 'value' in input
            ? input.value
            : input;
    const normalized = String(rawValue ?? '')
        .trim()
        .toLowerCase();
    if (normalized === 'outlinedark' || normalized === 'outline-dark') {
        return 'outlineDark';
    }
    if (normalized === 'primary') return 'primary';
    if (normalized === 'secondary') return 'secondary';
    if (normalized === 'outline') return 'outline';
    if (normalized === 'ghost') return 'ghost';
    if (normalized === 'link') return 'link';
    return 'primary';
};

const normalizeButtonSize = (input) => {
    const rawValue =
        input && typeof input === 'object' && 'value' in input
            ? input.value
            : input;
    const normalized = String(rawValue ?? '')
        .trim()
        .toLowerCase();
    if (normalized === 'small') return 'sm';
    if (normalized === 'medium') return 'md';
    if (normalized === 'large') return 'lg';
    if (
        normalized === 'x-large' ||
        normalized === 'xlarge' ||
        normalized === 'extra-large' ||
        normalized === 'extralarge'
    ) {
        return 'xl';
    }
    if (BUTTON_SIZES.has(normalized)) return normalized;
    return 'lg';
};

const normalizeThemeIconClass = (input) => {
    const normalized = String(input ?? '')
        .trim()
        .replace(/^['"]|['"]$/g, '')
        .replace(/\s+/g, ' ');
    if (!normalized || normalized.toLowerCase() === 'none') return '';
    return normalized;
};

const normalizeThemeIconPosition = (input, fallback = 'right') => {
    const rawValue =
        input && typeof input === 'object' && 'value' in input
            ? input.value
            : input;
    const normalized = String(rawValue ?? '')
        .trim()
        .toLowerCase();
    if (normalized === 'left') return 'left';
    if (normalized === 'right') return 'right';
    return fallback === 'left' ? 'left' : 'right';
};

const normalizeThemeIconSize = (input, fallback = 'medium') => {
    const rawValue =
        input && typeof input === 'object' && 'value' in input
            ? input.value
            : input;
    const normalized = String(rawValue ?? '')
        .trim()
        .toLowerCase();
    if (normalized === 'small') return 'small';
    if (normalized === 'large') return 'large';
    if (
        normalized === 'x-large' ||
        normalized === 'xlarge' ||
        normalized === 'extra-large' ||
        normalized === 'extralarge' ||
        normalized === 'xl'
    ) {
        return 'x-large';
    }
    if (normalized === 'medium') return 'medium';
    return ['small', 'medium', 'large', 'x-large'].includes(fallback)
        ? fallback
        : 'medium';
};

const normalizeBoolean = (input, fallback = true) => {
    const rawValue =
        input && typeof input === 'object' && 'value' in input
            ? input.value
            : input;
    if (rawValue == null) return fallback;
    if (typeof rawValue === 'boolean') return rawValue;
    if (typeof rawValue === 'number') return rawValue !== 0;
    if (typeof rawValue === 'string') {
        const normalized = rawValue.trim().toLowerCase();
        if (!normalized) return fallback;
        if (['false', '0', 'off', 'no'].includes(normalized)) return false;
        if (['true', '1', 'on', 'yes'].includes(normalized)) return true;
    }
    return fallback;
};

const readThemeIconVar = (name) => {
    if (typeof window === 'undefined') return '';
    return normalizeThemeIconClass(
        getComputedStyle(document.documentElement).getPropertyValue(name)
    );
};

const getThemeIconsForVariant = (variant) => ({
    default: readThemeIconVar(`--button-${variant}-icon-default`),
    hover: readThemeIconVar(`--button-${variant}-icon-hover`),
});

const readThemeIconPositionVar = (variant) => {
    if (typeof window === 'undefined') return 'right';
    return normalizeThemeIconPosition(
        getComputedStyle(document.documentElement).getPropertyValue(
            `--button-${variant}-icon-position`
        ),
        'right'
    );
};

const readThemeIconSizeVar = (variant) => {
    if (typeof window === 'undefined') return 'medium';
    return normalizeThemeIconSize(
        getComputedStyle(document.documentElement).getPropertyValue(
            `--button-${variant}-icon-size`
        ),
        'medium'
    );
};

const normalizeButtonHoverAnimation = (input) => {
    const normalized = String(input ?? '')
        .trim()
        .toLowerCase();
    if (normalized === 'slide') return 'slide-up';
    if (normalized === 'gradualfill') return 'gradual-fill';
    if (normalized === 'rotatinggradientborder') return 'rotating-border';
    if (normalized === 'rotatingborder') return 'rotating-border';
    if (normalized === 'underlinedraw') return 'underline-draw';
    if (normalized === 'gradientshift') return 'gradient-shift';
    if (normalized === 'pulsering') return 'pulse-ring';
    if (normalized === 'iconslide') return 'icon-slide';
    if (normalized === 'iconnudge') return 'icon-nudge';
    if (normalized === 'iconfillslide') return 'icon-fill-slide';
    if (normalized === 'iconcapsulesweep') return 'icon-show';
    if (normalized === 'iconhighlight') return 'icon-show';
    if (normalized === 'iconshow') return 'icon-show';
    if (normalized === 'iconreveal') return 'icon-reveal';
    return BUTTON_HOVER_ANIMATIONS.has(normalized) ? normalized : 'none';
};

const readThemeHoverAnimationVar = (variant) => {
    if (typeof window === 'undefined') return 'none';
    return normalizeButtonHoverAnimation(
        getComputedStyle(document.documentElement).getPropertyValue(
            `--button-${variant}-hover-animation`
        )
    );
};

const isSimpleTextChild = (value) =>
    typeof value === 'string' || typeof value === 'number';

const extractTextChild = (value) => {
    if (isSimpleTextChild(value)) return value;
    const nodes = Children.toArray(value);
    const meaningful = nodes.filter(
        (node) => !(typeof node === 'string' && node.trim() === '')
    );
    if (meaningful.length === 1 && isSimpleTextChild(meaningful[0])) {
        return meaningful[0];
    }
    return null;
};

const hasClassToken = (value, token) =>
    String(value || '')
        .split(/\s+/)
        .includes(token);

const buttonVariants = cva(
    'box-border inline-flex cursor-pointer items-center justify-center transition-[background,color,border-color,box-shadow,filter,transform,opacity] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] [--_btn-visual-border-width:0px] focus-visible:outline focus-visible:outline-0 focus-visible:outline-offset-2 disabled:pointer-events-none disabled:opacity-50',
    {
        variants: {
            variant: {
                primary: 'button-theme-primary border-0',
                secondary: 'button-theme-secondary border-0',
                outline:
                    'bg-transparent text-black [--_btn-visual-border-width:var(--button-outline-width)] [--button-outline-width:max(1px,var(--button-border-width,0px))] [border:0px_solid_transparent] [box-shadow:0_0_0_var(--button-outline-width)_#000] hover:bg-neutral-800 hover:text-white hover:[box-shadow:0_0_0_var(--button-outline-width)_#000]',
                outlineDark:
                    'bg-transparent text-white [--_btn-visual-border-width:var(--button-outline-width)] [--button-outline-width:max(1px,var(--button-border-width,0px))] [border:0px_solid_transparent] [box-shadow:0_0_0_var(--button-outline-width)_#fff] hover:bg-white hover:text-black hover:[box-shadow:0_0_0_var(--button-outline-width)_#fff]',
                ghost: 'bg-white text-black opacity-70 hover:bg-white hover:opacity-100',
                link: 'text-[var(--button-text-link-default)] underline-offset-4 hover:text-[var(--button-text-link-hover)] hover:underline',
            },
            size: {
                sm: 'rounded-[var(--shape-radius)] px-[var(--button-px)] py-[var(--button-py)] text-[length:calc(var(--button-sm-font-size,0.875rem)*var(--button-font-scale,1))] font-[var(--button-font-weight,700)] leading-[calc(var(--button-sm-line-height,1.25rem)*var(--button-font-scale,1))] [--button-px:var(--button-sm-px,1rem)] [--button-py:var(--button-sm-py,0.75rem)]',
                md: 'rounded-[var(--shape-radius)] px-[var(--button-px)] py-[var(--button-py)] text-[length:calc(var(--button-md-font-size,1.125rem)*var(--button-font-scale,1))] font-[var(--button-font-weight,700)] leading-[calc(var(--button-md-line-height,1.75rem)*var(--button-font-scale,1))] [--button-px:var(--button-md-px,1.25rem)] [--button-py:var(--button-md-py,1rem)]',
                lg: 'rounded-[var(--shape-radius)] px-[var(--button-px)] py-[var(--button-py)] text-[length:calc(var(--button-lg-font-size,1.25rem)*var(--button-font-scale,1))] font-[var(--button-font-weight,700)] leading-[calc(var(--button-lg-line-height,1.75rem)*var(--button-font-scale,1))] [--button-px:var(--button-lg-px,1.5rem)] [--button-py:var(--button-lg-py,1.25rem)]',
                xl: 'rounded-[var(--shape-radius)] px-[var(--button-px)] py-[var(--button-py)] text-[length:calc(var(--button-xl-font-size,1.5rem)*var(--button-font-scale,1))] font-[var(--button-font-weight,700)] leading-[calc(var(--button-xl-line-height,2rem)*var(--button-font-scale,1))] [--button-px:var(--button-xl-px,1.75rem)] [--button-py:var(--button-xl-py,1.5rem)]',
            },
        },
        defaultVariants: {
            variant: 'primary',
            size: 'lg',
        },
    }
);

const Button = forwardRef(
    (
        {
            className,
            style: styleProp,
            variant,
            size,
            iconDefault,
            iconHover,
            iconPosition,
            iconSize,
            iconVisible = true,
            inlineFieldKey,
            asChild = false,
            children,
            onMouseMove,
            onMouseLeave,
            ...props
        },
        ref
    ) => {
        const shouldRenderNiceJobAnchor =
            !asChild && hasClassToken(className, 'nj-lead');
        const Component = asChild
            ? Slot
            : shouldRenderNiceJobAnchor
            ? 'a'
            : 'button';
        const resolvedVariant = normalizeButtonVariant(variant);
        const resolvedSize = normalizeButtonSize(size);
        const [themeIcons, setThemeIcons] = useState({
            default: '',
            hover: '',
        });
        const [themeIconPosition, setThemeIconPosition] = useState('right');
        const [themeIconSize, setThemeIconSize] = useState('medium');
        const [hoverAnimation, setHoverAnimation] = useState('none');

        useEffect(() => {
            if (!THEME_ICON_VARIANTS.has(resolvedVariant)) {
                setThemeIcons((prev) =>
                    prev.default || prev.hover
                        ? { default: '', hover: '' }
                        : prev
                );
                return;
            }

            const next = getThemeIconsForVariant(resolvedVariant);
            setThemeIcons((prev) =>
                prev.default === next.default && prev.hover === next.hover
                    ? prev
                    : next
            );
        });

        useEffect(() => {
            if (!THEME_ICON_VARIANTS.has(resolvedVariant)) {
                setThemeIconPosition((prev) =>
                    prev === 'right' ? prev : 'right'
                );
                return;
            }

            const next = readThemeIconPositionVar(resolvedVariant);
            setThemeIconPosition((prev) => (prev === next ? prev : next));
        });

        useEffect(() => {
            if (!THEME_ICON_VARIANTS.has(resolvedVariant)) {
                setThemeIconSize((prev) =>
                    prev === 'medium' ? prev : 'medium'
                );
                return;
            }

            const next = readThemeIconSizeVar(resolvedVariant);
            setThemeIconSize((prev) => (prev === next ? prev : next));
        });

        useEffect(() => {
            if (!THEME_ICON_VARIANTS.has(resolvedVariant)) {
                setHoverAnimation((prev) => (prev === 'none' ? prev : 'none'));
                return;
            }
            const next = readThemeHoverAnimationVar(resolvedVariant);
            setHoverAnimation((prev) => (prev === next ? prev : next));
        });

        const slottableChild =
            asChild && isValidElement(children) ? children : null;
        const slottableInnerChildren = slottableChild?.props?.children;
        const textChild =
            extractTextChild(slottableInnerChildren) ??
            extractTextChild(children);
        const childIsSimpleText = textChild !== null;
        const iconVisibleValue = normalizeBoolean(iconVisible, true);
        const resolvedDefaultIcon = normalizeThemeIconClass(iconDefault);
        const resolvedHoverIcon = normalizeThemeIconClass(iconHover);
        const defaultIconClass = resolvedDefaultIcon || themeIcons.default;
        const hoverIconClass =
            resolvedHoverIcon ||
            resolvedDefaultIcon ||
            themeIcons.hover ||
            themeIcons.default;
        const hasDistinctHoverIcon =
            !!hoverIconClass && hoverIconClass !== defaultIconClass;
        const shouldRenderThemeIcon =
            childIsSimpleText &&
            iconVisibleValue &&
            !!defaultIconClass &&
            (THEME_ICON_VARIANTS.has(resolvedVariant) ||
                !!resolvedDefaultIcon ||
                !!resolvedHoverIcon);
        const resolvedIconPosition = normalizeThemeIconPosition(
            iconPosition,
            themeIconPosition
        );
        const resolvedIconSize = normalizeThemeIconSize(
            iconSize,
            themeIconSize
        );
        const shouldUseGradientBorderShell =
            resolvedVariant === 'primary' || resolvedVariant === 'secondary';
        const shouldEnableSlideAnimation =
            shouldUseGradientBorderShell &&
            (hoverAnimation === 'slide-up' || hoverAnimation === 'slide-right');
        const shouldCompensateExternalBorder =
            resolvedVariant === 'primary' ||
            resolvedVariant === 'secondary' ||
            resolvedVariant === 'outline' ||
            resolvedVariant === 'outlineDark';
        const shouldEnableMagnet =
            shouldUseGradientBorderShell && hoverAnimation === 'magnet';
        const shouldEnableTilt =
            shouldUseGradientBorderShell && hoverAnimation === 'tilt';
        const shouldTrackPointer =
            shouldUseGradientBorderShell &&
            (shouldEnableMagnet || shouldEnableTilt);
        const mergedStyle = shouldCompensateExternalBorder
            ? {
                  margin: 'var(--_btn-visual-border-width)',
                  ...styleProp,
              }
            : styleProp;

        const buttonContent = useMemo(() => {
            const backgroundLayers = shouldUseGradientBorderShell ? (
                <>
                    <span className="button-bg-border" aria-hidden="true" />
                    <span className="button-bg-fill" aria-hidden="true" />
                    <span className="button-bg-hover" aria-hidden="true" />
                </>
            ) : null;
            const textNode = (
                <InlineEditableText
                    fieldKey={inlineFieldKey}
                    className={cn(
                        'button-label relative z-[1] inline-flex flex-col items-center justify-center text-center',
                        shouldEnableSlideAnimation && 'button-slide-text'
                    )}
                >
                    {textChild}
                </InlineEditableText>
            );
            if (!shouldRenderThemeIcon) {
                if (!childIsSimpleText) {
                    if (slottableChild) {
                        return cloneElement(slottableChild, {
                            children: (
                                <>
                                    {backgroundLayers}
                                    <span className="button-content relative z-[2] inline-flex items-center justify-center">
                                        {slottableInnerChildren}
                                    </span>
                                </>
                            ),
                        });
                    }
                    return (
                        <>
                            {backgroundLayers}
                            <span className="button-content relative z-[2] inline-flex items-center justify-center">
                                {children}
                            </span>
                        </>
                    );
                }
                if (
                    slottableChild &&
                    extractTextChild(slottableInnerChildren)
                ) {
                    return cloneElement(slottableChild, {
                        children: (
                            <>
                                {backgroundLayers}
                                {textNode}
                            </>
                        ),
                    });
                }
                return (
                    <>
                        {backgroundLayers}
                        {textNode}
                    </>
                );
            }
            const iconNode = (
                <span
                    className={cn(
                        'button-icon relative z-[1] inline-flex h-[1em] w-[1em] shrink-0 items-center justify-center leading-none',
                        resolvedIconSize === 'small'
                            ? 'text-[0.9em]'
                            : resolvedIconSize === 'x-large'
                            ? 'text-[1.3em]'
                            : resolvedIconSize === 'large'
                            ? 'text-[1.15em]'
                            : 'text-[1em]',
                        shouldEnableSlideAnimation && 'button-slide-icon'
                    )}
                >
                    <IconValue
                        value={defaultIconClass}
                        className={cn(
                            'fa-fw block leading-none text-current transition-opacity duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]',
                            hasDistinctHoverIcon
                                ? 'opacity-100 group-hover/button:opacity-0'
                                : 'opacity-100'
                        )}
                    />
                    {hasDistinctHoverIcon && (
                        <IconValue
                            value={hoverIconClass}
                            className={cn(
                                'fa-fw absolute inset-0 block leading-none text-current opacity-0 transition-opacity duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover/button:opacity-100'
                            )}
                        />
                    )}
                </span>
            );
            const themedChildren = (
                <>
                    {resolvedIconPosition === 'left' ? iconNode : textNode}
                    {resolvedIconPosition === 'left' ? textNode : iconNode}
                </>
            );
            if (slottableChild && extractTextChild(slottableInnerChildren)) {
                return cloneElement(slottableChild, {
                    children: (
                        <>
                            {backgroundLayers}
                            {themedChildren}
                        </>
                    ),
                });
            }
            return (
                <>
                    {backgroundLayers}
                    {themedChildren}
                </>
            );
        }, [
            children,
            hasDistinctHoverIcon,
            hoverIconClass,
            defaultIconClass,
            resolvedIconPosition,
            resolvedIconSize,
            slottableChild,
            slottableInnerChildren,
            shouldRenderThemeIcon,
            shouldEnableSlideAnimation,
            shouldUseGradientBorderShell,
            childIsSimpleText,
            textChild,
        ]);

        return (
            <Component
                className={cn(
                    buttonVariants({
                        variant: BUTTON_VARIANTS.has(resolvedVariant)
                            ? resolvedVariant
                            : 'primary',
                        size: resolvedSize,
                    }),
                    shouldUseGradientBorderShell && 'button-gradient-border',
                    shouldUseGradientBorderShell &&
                        hoverAnimation !== 'none' &&
                        `button-hover-${hoverAnimation}`,
                    shouldRenderThemeIcon && 'group/button gap-2',
                    className
                )}
                data-button-variant={resolvedVariant}
                data-icon-position={resolvedIconPosition}
                style={mergedStyle}
                ref={ref}
                {...(shouldRenderNiceJobAnchor
                    ? { href: props.href || '#' }
                    : {})}
                onMouseMove={(event) => {
                    if (shouldTrackPointer) {
                        const target = event.currentTarget;
                        const rect = target.getBoundingClientRect();
                        const x = event.clientX - rect.left;
                        const y = event.clientY - rect.top;
                        target.style.setProperty('--button-magnet-x', `${x}px`);
                        target.style.setProperty('--button-magnet-y', `${y}px`);
                        if (shouldEnableTilt) {
                            const xRatio = (x / rect.width) * 2 - 1;
                            const yRatio = (y / rect.height) * 2 - 1;
                            const rotateY = Math.max(
                                -6,
                                Math.min(6, xRatio * 6)
                            );
                            const rotateX = Math.max(
                                -6,
                                Math.min(6, -yRatio * 6)
                            );
                            target.style.setProperty(
                                '--button-tilt-rotate-x',
                                `${rotateX.toFixed(2)}deg`
                            );
                            target.style.setProperty(
                                '--button-tilt-rotate-y',
                                `${rotateY.toFixed(2)}deg`
                            );
                        }
                    }
                    if (typeof onMouseMove === 'function') onMouseMove(event);
                }}
                onMouseLeave={(event) => {
                    if (shouldTrackPointer) {
                        const target = event.currentTarget;
                        target.style.setProperty('--button-magnet-x', '50%');
                        target.style.setProperty('--button-magnet-y', '50%');
                        target.style.setProperty(
                            '--button-tilt-rotate-x',
                            '0deg'
                        );
                        target.style.setProperty(
                            '--button-tilt-rotate-y',
                            '0deg'
                        );
                    }
                    if (typeof onMouseLeave === 'function') onMouseLeave(event);
                }}
                {...props}
            >
                {buttonContent}
            </Component>
        );
    }
);

Button.displayName = 'Button';

export { Button, buttonVariants };
